import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "@/config/api";
import { getAccessToken } from "@/utils/token_functions";
import { secureAxios } from "@/middleware/encryptionMiddleware";

export const createEvent = createAsyncThunk(
  "event/createEvent",
  async (eventData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();

      const response = await secureAxios.post(
        `${BASE_URL}/v1/event`,
        eventData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        console.error("Unauthorized: Token might be invalid");
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },

  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Update the fetchEvents thunk to accept pagination parameters
export const fetchEvents = createAsyncThunk(
  "event/fetchEvents",
  async ({ skip, limit }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.get(`${BASE_URL}/v1/event`, {
        params: {
          skip,
          limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return {
        events: response.data.data,
        total: response.data?.data?.length, // Hardcoded total for now, update with actual total from API
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },

  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// thunk for fetching single or multiple events
export const fetchEventByAny = createAsyncThunk(
  "event/fetchEventByAny",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.get(
        `${BASE_URL}/v1/event/by-any/${key}/${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return {
        data: response.data?.data,
        total: response.data?.data?.length, // Include the total count
        isSingleEvent: key === "_id", // Flag to indicate if we're fetching by ID
      };
    } catch (error) {
      // If it's a 404 error (not found), return an empty array instead of rejecting
      if (error.response?.status === 404) {
        return { data: [], total: 0, isSingleEvent: false }; // Return empty array for "not found" instead of rejecting
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },

  {
    meta: {
      requiresNetwork: true,
      hasEncryptedPayload: true,
      requiresDecryption: true,
    },
  }
);

export const updateEvent = createAsyncThunk(
  "event/updateEvent",
  async (eventData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.patch(
        `${BASE_URL}/v1/event`, // No ID in the URL
        eventData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },

  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Update delete event thunk to use secureAxios
export const deleteEvent = createAsyncThunk(
  "event/deleteEvent",
  async (eventId, { rejectWithValue }) => {
    try {
      const token = getAccessToken();

      // Use secureAxios with proper headers
      const response = await secureAxios.delete(`${BASE_URL}/v1/event`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: [eventId], // Send directly as array: ["eventId"]
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

const initialState = {
  events: [],
  currentEvent: null,
  fetchedEvents: [],
  total: 0,
  eventDetails: {
    name: "",
    type: "",
    description: "",
    schedule: {
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
    },
    location: [], // Array of locations
    fullName: "",
  },
  isLoading: false,
  error: null,
  success: false,
  lastFetchTime: null,
  isOfflineData: false,
};

const eventSlice = createSlice({
  name: "event",
  initialState,
  reducers: {
    updateEventDetails: (state, action) => {
      state.eventDetails = {
        ...state.eventDetails,
        ...action.payload,
      };
    },
    updateSchedule: (state, action) => {
      state.eventDetails.schedule = {
        ...state.eventDetails.schedule,
        ...action.payload,
      };
    },
    addLocation: (state, action) => {
      state.eventDetails.location.push(action.payload);
    },
    removeLocation: (state, action) => {
      state.eventDetails.location = state.eventDetails.location.filter(
        (loc) => loc !== action.payload
      );
    },
    resetEventState: () => initialState,
    setOfflineMode: (state, action) => {
      state.isOfflineData = action.payload;
    },
    loadCachedData: (state, action) => {
      return {
        ...state,
        ...action.payload,
        isOfflineData: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createEvent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createEvent.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchEvents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.events = action.payload.events;
        state.total = action.payload.total;
        state.error = null;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchEventByAny.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEventByAny.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fetchedEvents = action.payload.data;
        state.total = action.payload.total; // Store the total in state

        if (action.payload.isSingleEvent && action.payload?.data?.length > 0) {
          // If we're fetching by ID, store as currentEvent
          state.currentEvent = action.payload.data[0];
        } else {
          state.currentEvent =
            action.payload?.data?.length > 0 ? action.payload.data[0] : null;
        }
        state.error = null;
      })
      .addCase(fetchEventByAny.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateEvent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateEvent.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteEvent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteEvent.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Export actions
export const {
  updateEventDetails,
  updateSchedule,
  addLocation,
  removeLocation,
  resetEventState,
  setOfflineMode,
  loadCachedData,
} = eventSlice.actions;

// Export selectors
export const selectEventDetails = (state) => state.event.eventDetails;
export const selectEventLoading = (state) => state.event.isLoading;
export const selectEventError = (state) => state.event.error;
export const selectEventSuccess = (state) => state.event.success;
export const selectAllEvents = (state) => state.event.events;
export const selectCurrentEvent = (state) => state.event.currentEvent;
export const selectEventTotal = (state) => state.event.total;
export const selectEventsByManifesto = (state) => {
  // Check if we should return fetchedEvents (multiple) or use currentEvent
  if (state.event.fetchedEvents && state.event?.fetchedEvents?.length > 0) {
    return state.event.fetchedEvents;
  }
  // Fall back to currentEvent for backward compatibility
  const event = state.event.currentEvent;
  return event ? [event] : [];
};
export const selectIsOfflineData = (state) =>
  state.event.isOfflineData || false;
export const selectFetchedEvents = (state) => state.event.fetchedEvents || [];

export default eventSlice.reducer;
